/**
 * パスワードバリデーションユーティリティ
 * AWS Cognitoのパスワードポリシーに準拠
 */

/**
 * パスワードバリデーション結果
 */
export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    symbol: boolean;
    allowedChars: boolean;
  };
}

/**
 * パスワード要件の定数
 */
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  // 許可する記号: JSON/シェルで安全な記号のみ
  // 除外: " \ ` | ~ ' (JSONエスケープ・シェルインジェクション対策)
  ALLOWED_SYMBOLS: '!@#$%^&*()-_=+[]{};:,.<>/?',
  ALLOWED_SYMBOLS_REGEX: /[!@#$%^&*()\-_=+\[\]{};:,.<>/?]/,
  // 全体の文字種チェック（英数字 + 許可された記号のみ）
  ALLOWED_CHARS_REGEX: /^[A-Za-z0-9!@#$%^&*()\-_=+\[\]{};:,.<>/?]+$/,
} as const;

/**
 * パスワードの各条件をチェック
 */
export function checkPasswordRequirements(password: string): PasswordValidationResult['checks'] {
  return {
    length: password.length >= PASSWORD_REQUIREMENTS.MIN_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: PASSWORD_REQUIREMENTS.ALLOWED_SYMBOLS_REGEX.test(password),
    allowedChars: PASSWORD_REQUIREMENTS.ALLOWED_CHARS_REGEX.test(password),
  };
}

/**
 * パスワードをバリデーションする
 * @param password - 検証するパスワード
 * @returns バリデーション結果
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  const checks = checkPasswordRequirements(password);

  if (!password) {
    return {
      valid: false,
      errors: ['パスワードは必須です'],
      checks,
    };
  }

  if (!checks.length) {
    errors.push(`パスワードは${PASSWORD_REQUIREMENTS.MIN_LENGTH}文字以上である必要があります`);
  }

  if (!checks.allowedChars) {
    errors.push('パスワードに使用できない文字が含まれています');
  }

  if (!checks.uppercase) {
    errors.push('パスワードには大文字（A-Z）を1文字以上含める必要があります');
  }

  if (!checks.lowercase) {
    errors.push('パスワードには小文字（a-z）を1文字以上含める必要があります');
  }

  if (!checks.number) {
    errors.push('パスワードには数字（0-9）を1文字以上含める必要があります');
  }

  if (!checks.symbol) {
    errors.push(`パスワードには記号（${PASSWORD_REQUIREMENTS.ALLOWED_SYMBOLS}）を1文字以上含める必要があります`);
  }

  return {
    valid: errors.length === 0,
    errors,
    checks,
  };
}

/**
 * パスワード要件をテキストで取得
 */
export function getPasswordRequirementsText(): string {
  return `最小${PASSWORD_REQUIREMENTS.MIN_LENGTH}文字、大文字・小文字・数字・記号（${PASSWORD_REQUIREMENTS.ALLOWED_SYMBOLS}）を含む`;
}
